import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, Badge, Button, Card, CardContent, CardHeader, CardTitle, SkeletonCard } from "@/components/ui";
import { getErrorMessage, isApiError } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS } from "@/api/types/booking";
import {
  PARTY_ADDON_LABELS,
  PARTY_ADDON_UNIT_PRICE_CENTS,
  PARTY_FORMAT_LABELS,
  PARTY_OCCASION_LABELS,
  type PartyAddon,
  type PartyAddonType,
} from "@/api/types/party";
import { usePartyPortalMe, useRequestPartyAddon } from "./usePartyPortal";

const BUSINESS_NAME = "The Fox & The Owl";
const INVALID_LINK_MESSAGE = "This link is invalid or has expired — please ask The Fox & The Owl for a new one.";

/**
 * PUBLIC party portal (Phase 2d) — reached via a magic-link URL
 * (`/party-portal?token=<jwt>`), never behind the staff auth guard (see
 * App.tsx: this route sits outside <RequireAuth>). Same "dedicated axios
 * client, enabled: !!token, identical invalid-link posture" shape as
 * features/portal/MembershipPortalPage.tsx — a completely separate
 * token/backend surface, not an extension of it. Scope this phase: party
 * detail (base price, current add-ons, suggested add-ons) + a
 * request-add-on action (see app/api/portal_party.py's module docstring).
 */
export function PartyPortalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { data, isLoading, error, refetch } = usePartyPortalMe(token);
  const isInvalidToken = isApiError(error) && error.response?.status === 401;
  const requestAddon = useRequestPartyAddon(token);

  const [pendingAddon, setPendingAddon] = useState<PartyAddonType | null>(null);

  async function handleRequestAddon(addonType: PartyAddonType) {
    setPendingAddon(addonType);
    try {
      await requestAddon.mutateAsync({ addon_type: addonType, quantity: data?.headcount || 1 });
      toastSuccess("Add-on requested", PARTY_ADDON_LABELS[addonType]);
    } catch (err) {
      toastError("Couldn't request add-on", getErrorMessage(err));
    } finally {
      setPendingAddon(null);
    }
  }

  const remainingSuggestions = data ? data.suggested_addons.filter((s) => !data.addons.some((a) => a.addon_type === s)) : [];

  function addonLabel(addon: PartyAddon): string {
    return addon.catalog_item_name ?? PARTY_ADDON_LABELS[addon.addon_type];
  }

  return (
    <div className="min-h-screen bg-bg-body px-4 py-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-wide text-copper">{BUSINESS_NAME}</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-text-primary sm:text-3xl">Your party</h1>
        </header>

        {!token || isInvalidToken ? (
          <PartyPortalInvalidLink />
        ) : isLoading ? (
          <div className="flex flex-col gap-6">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : error || !data ? (
          <ApiError error={error} onRetry={() => void refetch()} title="Unable to load your party" />
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle>{data.resource_name}</CardTitle>
                  <p className="mt-1 text-sm text-text-secondary">
                    {data.booking_date} · {PARTY_OCCASION_LABELS[data.occasion]} ·{" "}
                    {PARTY_FORMAT_LABELS[data.format]}
                    {data.headcount ? ` · ${data.headcount} kids` : ""}
                  </p>
                </div>
                <Badge variant={BOOKING_STATUS_BADGE_VARIANT[data.status]}>{BOOKING_STATUS_LABELS[data.status]}</Badge>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-text-secondary">Base price: {centsToUsd(data.base_price_cents)}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add-ons</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.addons.length === 0 ? (
                  <p className="text-sm text-text-secondary">No add-ons yet.</p>
                ) : (
                  <ul className="divide-y divide-border" data-testid="party-portal-addons">
                    {data.addons.map((addon) => (
                      <li key={addon.id} className="flex items-center justify-between py-2 text-sm">
                        <span className="text-text-primary">
                          {addonLabel(addon)} x{addon.quantity}
                        </span>
                        <span className="text-text-secondary">{centsToUsd(addon.total_cents)}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {remainingSuggestions.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-semibold text-text-primary">Want to add any of these?</p>
                    <div className="flex flex-wrap gap-2">
                      {remainingSuggestions.map((addonType) => (
                        <Button
                          key={addonType}
                          size="sm"
                          variant="secondary"
                          disabled={requestAddon.isPending && pendingAddon === addonType}
                          onClick={() => void handleRequestAddon(addonType)}
                          data-testid={`request-addon-${addonType}`}
                        >
                          + {PARTY_ADDON_LABELS[addonType]} ({centsToUsd(PARTY_ADDON_UNIT_PRICE_CENTS[addonType])}/ea)
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="pt-2 text-lg font-semibold text-text-primary" data-testid="party-portal-total">
                  Total: {centsToUsd(data.total_price_cents)}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function PartyPortalInvalidLink() {
  return (
    <Card className="text-center">
      <CardContent className="flex flex-col items-center gap-3 py-8">
        <span className="text-4xl" role="img" aria-label="Locked link">
          🔒
        </span>
        <p className="text-lg font-semibold text-text-primary">Link invalid or expired</p>
        <p className="max-w-sm text-sm text-text-secondary">{INVALID_LINK_MESSAGE}</p>
      </CardContent>
    </Card>
  );
}
